import { CompactText } from 'ui/CompactText';
import { ProfileCard, ProfileField, ProfileGrid } from 'ui/ProfileCard';
import { LetterIcon, PhoneIcon, UserIcon, UsersIcon } from 'ui/icons';
import type { FC } from 'react';
import { PageHead } from 'ui/kit/PageHead';
import { Link } from 'react-router';
import { Pencil, Settings } from 'lucide-react';

import { useAuthSession } from 'hooks/useAuthSession';
import { sexShortLabel } from 'utils/sexLabels';
import { FadeIn } from 'ui/kit/uiMotion';

import {
  formatEmailDisplay,
  formatPhoneDisplay,
  hasCompleteContacts,
  isEmailComplete,
  isPhoneComplete,
} from './contactUtils';
import { ProfileContactsSection } from './ProfileContactsSection';
import classes from './StudentProfileView.module.scss';
import { studentParticipantLabel, studentStatusChip } from './studentProfileScreen';
import type { IProfile } from '../model/types';

type Props = {
  profile: IProfile;
  onEdit: () => void;
};

export const StudentProfileView: FC<Props> = ({ profile, onEdit }) => {
  const { data: session } = useAuthSession();
  const chip = studentStatusChip(profile.verification_status || '');
  const phone = session?.phone ?? profile.phone ?? '';
  const email = session?.email ?? profile.email ?? '';
  const phoneVerified = Boolean(session?.phone_verified ?? session?.phone_verified_at);
  const emailVerified = Boolean(session?.email_verified ?? session?.email_verified_at);
  const contactsReady = hasCompleteContacts(phone, email);
  const sex = sexShortLabel(profile.sex);
  const kicker =
    profile.verification_status === 'approved' ? 'Данные профиля проверены' : null;

  let contactStatus = 'Телефон и почта видны только тебе.';
  if (!isPhoneComplete(phone)) {
    contactStatus = 'Укажи телефон, чтобы с тобой могли связаться по заявке. Почта необязательна.';
  } else if ((email || '').trim() && !isEmailComplete(email)) {
    contactStatus = 'Если указываешь почту, проверь адрес.';
  } else if (!phoneVerified || (isEmailComplete(email) && !emailVerified)) {
    contactStatus = 'Подтверждение еще не завершено. Контакты скрыты от других.';
  } else if (!contactsReady) {
    contactStatus = 'Почту можно не указывать. Телефон виден только тебе.';
  }

  return (
    <FadeIn>
      <main className={classes.page}>
        <PageHead crumb={<>Личный кабинет / Мой профиль</>} title={<>Мой профиль</>} />
        {kicker ? <p className={classes.kicker}>{kicker}</p> : null}

        <ProfileGrid className={classes.grid}>
          <ProfileCard
            icon={<UserIcon />}
            title={profile.name || 'Профиль'}
            className={classes.mainCard}
            actions={
              /* r4: действия внутри карточки профиля - пара равной ширины (основная + вторичная). */
              <div className={classes.cardActions}>
                <button type="button" className={classes.primary} onClick={onEdit}>
                  <Pencil size={18} strokeWidth={2.2} aria-hidden />
                  <span>Редактировать</span>
                </button>
                <Link to="/profile/settings" className={classes.secondary}>
                  <Settings size={18} strokeWidth={2.2} aria-hidden />
                  <span>Настройки</span>
                </Link>
              </div>
            }
          >
            {chip ? (
              <span className={classes.chip}>
                <span className={classes.chipDot} aria-hidden />
                {chip}
              </span>
            ) : null}
            <ProfileField icon={<UsersIcon />} label='Участник'>
              {studentParticipantLabel(profile.registrant_type)}
            </ProfileField>
            {sex ? (
              <ProfileField icon={<UserIcon />} label='Пол'>
                {sex}
              </ProfileField>
            ) : null}
          </ProfileCard>

          <ProfileCard icon={<PhoneIcon />} title='Контакты'>
            {contactsReady ? (
              <>
                <ProfileField icon={<LetterIcon />} label='Почта'>
                  <span className={classes.valueEllipsis} title={email || undefined}>
                    {formatEmailDisplay(email)}
                  </span>
                </ProfileField>
                <ProfileField icon={<PhoneIcon />} label='Телефон'>
                  {formatPhoneDisplay(phone)}
                </ProfileField>
              </>
            ) : (
              <div className={classes.contacts}>
                <ProfileContactsSection />
              </div>
            )}
            <CompactText className={classes.noteText} tone={'info'}>
              {contactStatus} Изменить можно в настройках.
            </CompactText>
          </ProfileCard>

        </ProfileGrid>

      </main>
    </FadeIn>
  );
};
